import React, { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, ShoppingCart, Loader, Plus, Minus, Globe, MapPin } from "lucide-react";
import axios from "axios";
import { CartContext } from "./CartContext";
import { API_BASE_URL } from "../config";

const QuickShop = ({ isOpen, onClose }) => {
  const {
    cartItems,
    addToCart,
    selectedRegion,
    setSelectedRegion,
    minOrderAmounts
  } = useContext(CartContext);

  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCat, setSelectedCat] = useState("All");
  const [regionError, setRegionError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError("");
      axios
        .get(`${API_BASE_URL}/all-data`)
        .then((res) => {
          setProducts(res.data || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error loading products for quick shop:", err);
          setError("Failed to load products.");
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  const categories = ["All", ...new Set(products.map((p) => p.name).filter(Boolean))];

  // Filter products by search and category
  const filteredList = [];
  products.forEach((prod) => {
    if (selectedCat !== "All" && prod.name?.toLowerCase() !== selectedCat.toLowerCase()) {
      return;
    }
    const subs = prod.subProducts || prod.subproducts || [];
    subs.forEach((sub) => {
      const matchSearch =
        (prod.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.subName || "").toLowerCase().includes(searchTerm.toLowerCase());

      if (matchSearch) {
        filteredList.push({
          ...sub,
          proId: prod.pro_id,
          categoryName: prod.name
        });
      }
    });
  });

  const getQtyInCart = (proId, subId) => {
    return cartItems.find((item) => item.proId === proId && item.subId === subId)?.quantity || 0;
  };

  const handleUpdateQty = (item, newQty) => {
    const parsedQty = parseInt(newQty, 10);
    if (isNaN(parsedQty) || parsedQty < 0) return;

    const rateObj = (item.rateEntities || item.rateentities || [])[0] || {};
    const itemToAdd = {
      proId: item.proId,
      subId: item.sub_id,
      name: item.subName || "Cracker Item",
      quantity: parsedQty,
      rate: rateObj.rate || 0,
      discount: rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0)
    };
    addToCart(itemToAdd);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black z-[1040]"
          />

          {/* Drawer Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed top-0 right-0 w-full sm:w-[450px] h-full bg-[#FAF8F5] shadow-2xl z-[1050] overflow-hidden flex flex-col justify-between font-sans border-l border-blue-100"
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-950 to-blue-900 text-white">
              <div className="flex items-center gap-2">
                <ShoppingCart size={20} className="text-yellow-400" />
                <h2 className="text-lg font-black tracking-wide font-serif">Quick Shop Panel</h2>
                <span className="text-xs bg-blue-800 px-2 py-0.5 rounded-full font-bold">
                  Fast Add
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-yellow-400 p-1 bg-white/10 hover:bg-white/20 rounded-lg transition"
                aria-label="Close panel"
              >
                <X size={18} />
              </button>
            </div>

            {/* Filter Section */}
            <div className="p-4 bg-white border-b border-gray-100 space-y-3 shrink-0">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search cracker name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-blue-900 focus:outline-none bg-gray-50/50"
                />
              </div>

              {/* Category Pills */}
              <div className="flex gap-1.5 overflow-x-auto pb-1.5 scrollbar-thin">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCat(cat)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition whitespace-nowrap border shrink-0 ${
                      selectedCat === cat
                        ? "bg-blue-900 text-white border-blue-950 shadow-sm"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {isLoading ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-2 py-20 text-gray-500">
                  <Loader className="animate-spin text-blue-900" size={24} />
                  <p className="text-xs font-bold">Loading quick shop catalog...</p>
                </div>
              ) : error ? (
                <p className="text-center py-20 text-xs font-bold text-red-600">⚠️ {error}</p>
              ) : filteredList.length === 0 ? (
                <p className="text-center py-20 text-xs font-bold text-gray-400">No matching items.</p>
              ) : (
                <div className="space-y-3">
                  {filteredList.map((item) => {
                    const rateObj = (item.rateEntities || item.rateentities || [])[0] || {};
                    const rate = rateObj.rate || 0;
                    const discount = rateObj.Discount !== undefined ? rateObj.Discount : (rateObj.discount || 0);
                    const discountRate = Math.round(rate * (1 - discount / 100));
                    const cartQty = getQtyInCart(item.proId, item.sub_id);

                    return (
                      <div
                        key={item.sub_id}
                        className="p-3 bg-white border border-gray-150 rounded-2xl flex items-center justify-between gap-4 hover:shadow-xs transition"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {item.childimg && (
                            <img
                              src={item.childimg}
                              alt={item.subName}
                              className="w-12 h-12 object-cover rounded-xl border border-gray-100 shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="text-[9px] uppercase font-black text-blue-900 tracking-wider block">
                              {item.categoryName}
                            </span>
                            <h4 className="font-bold text-gray-800 text-sm truncate capitalize">
                              {item.subName}
                            </h4>
                            <div className="flex items-center gap-2 text-xs mt-0.5">
                              <span className="font-black text-green-700">₹{discountRate}</span>
                              {discount > 0 && (
                                <span className="line-through text-gray-400 text-[10px]">₹{rate}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Quantity controls */}
                        <div className="flex items-center gap-1.5 shrink-0 bg-gray-50 border rounded-xl p-0.5">
                          <button
                            onClick={() => handleUpdateQty(item, Math.max(0, cartQty - 1))}
                            className="w-6 h-6 rounded-lg bg-white border text-blue-900 font-bold hover:bg-blue-50 flex items-center justify-center transition text-xs"
                          >
                            <Minus size={10} />
                          </button>
                          <input
                            type="number"
                            min="0"
                            value={cartQty}
                            onChange={(e) => handleUpdateQty(item, e.target.value)}
                            className="w-10 text-center text-xs font-black text-blue-950 bg-transparent border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button
                            onClick={() => handleUpdateQty(item, cartQty + 1)}
                            className="w-6 h-6 rounded-lg bg-white border text-blue-900 font-bold hover:bg-blue-50 flex items-center justify-center transition text-xs"
                          >
                            <Plus size={10} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-white shrink-0 space-y-3">
              {/* Delivery Region Selector */}
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <Globe size={16} className="text-blue-900 shrink-0" />
                  <select
                    onChange={(e) => {
                      setSelectedRegion(e.target.value);
                      setRegionError("");
                    }}
                    value={selectedRegion}
                    className="w-full p-2 border border-gray-200 rounded-xl text-xs bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-900"
                  >
                    <option value="" disabled>-- Select State / Region --</option>
                    {Object.keys(minOrderAmounts).map((region) => {
                      const hasLimit = minOrderAmounts[region] ? true : false;
                      return (
                        <option key={region} value={region}>
                          {region}{hasLimit ? ` (Min: ₹${minOrderAmounts[region]})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                {selectedRegion && minOrderAmounts[selectedRegion] ? (
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500">
                    <MapPin size={12} className="text-green-600" />
                    <span>Delivery to: <span className="text-blue-900">{selectedRegion}</span></span>
                    <span className="ml-auto">Min: ₹{minOrderAmounts[selectedRegion]}</span>
                  </div>
                ) : selectedRegion ? (
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-green-700">
                    <span>✅ No minimum order limit for {selectedRegion}</span>
                  </div>
                ) : null}
                {regionError && (
                  <p className="text-red-600 text-[10px] font-bold">⚠️ {regionError}</p>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-full bg-blue-900 hover:bg-blue-950 text-white py-3 rounded-2xl font-black text-xs uppercase tracking-wider transition shadow-md"
              >
                Close Quick Shop
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuickShop;
